import { Throwable } from "@wss/error-tracking/throwable";
import { toError } from "@wss/error-tracking/utils";
import ReactDOM from "react-dom/client";
import React from "react";
import { RelatedResource } from "./BlogPostContent/BlogRelatedResources";
import SubmissionResult from "./Shared/SubmissionResult";
export const defaultLayoutId = 1;
export const fullWidthLayoutId = 2;
type BlogLayout = typeof defaultLayoutId | typeof fullWidthLayoutId;
export type BlogPost = {
  title: string;
  id: number;
  seoUrl: string;
  content: string;
  datePosted: string;
  userName: string;
  isCouponUpdate: boolean;
  headerImagePath: string;
  featuredSocialImagePath: string;
  pinterestImagePath: string;
  shouldHideCouponFooter: boolean;
  isPublished: boolean;
  redirectLink: string;
  authorDisplayName: string;
  blogLayoutId: BlogLayout;
  categories: {
    url: string;
    name: string;
  }[];
  link: string;
  pinterestShareUrl: string;
  twitterShareUrl: string;
  facebookShareUrl: string;
  relatedResources?: RelatedResource[];
};
export interface DynamicEmailSignupElements {
  emailSignupButton: HTMLButtonElement;
  emailSignupForm: HTMLFormElement;
}
export type EmailSignupEndpointResponse = {
  statusCode: number;
};
export const getEmailElementsFromDynamicContent = (blogContentRef: React.RefObject<HTMLDivElement>): Throwable<DynamicEmailSignupElements> => {
  try {
    const emailSignupButton = blogContentRef?.current?.querySelector(".mailer-signup-btn");
    const emailSignupForm = blogContentRef?.current?.querySelector(".email-form");
    if (!(emailSignupButton instanceof HTMLButtonElement) || !(emailSignupForm instanceof HTMLFormElement)) {
      throw new Error("Email signup button or form not found");
    }
    return {
      isError: false,
      value: {
        emailSignupButton,
        emailSignupForm
      }
    };
  } catch (error) {
    return {
      isError: true,
      error: toError(error)
    };
  }
};
export const getEmailFormDataFromDynamicBlogContent = (eventInfo: SubmitEvent): Throwable<URLSearchParams> => {
  try {
    if (!(eventInfo?.target instanceof HTMLFormElement)) {
      throw new Error("Event target is not a form element");
    }
    const emailInputElement = eventInfo.target.elements.namedItem("emailaddress") ?? eventInfo.target.elements.namedItem("email");
    if (!(emailInputElement instanceof HTMLInputElement)) {
      throw new Error("Email input element not found");
    }
    return {
      isError: false,
      value: new URLSearchParams({
        emailAddress: emailInputElement.value
      })
    };
  } catch (error) {
    return {
      isError: true,
      error: toError(error)
    };
  }
};
export const addAttributesToDynamicFormContent = (elements: DynamicEmailSignupElements) => {
  if (elements) {
    elements.emailSignupForm.setAttribute("data-testid", "embedded-email-signup-form");
    elements.emailSignupButton.setAttribute("data-testid", "embedded-email-signup-button");
    elements.emailSignupForm.querySelectorAll("input").forEach(input => {
      if (input instanceof HTMLInputElement) {
        input.required = true;
        if (input.name === "emailaddress" || input.name === "email") {
          input.type = "email";
        }
      }
    });
  }
};
export const updateDynamicFormContent = (emailSignupForm: HTMLFormElement, success: boolean) => {
  emailSignupForm.innerHTML = "";
  const resultDiv = document.createElement("div");
  resultDiv.className = "m-0 mx-auto mb-8 w-4/5";
  resultDiv.setAttribute("data-testid", "email-signup-submission-result");
  emailSignupForm.appendChild(resultDiv);

  // Since blog content is dynamically and inserted using dangerouslySetInnerHTML, React can't
  // update it directly so we need to use createRoot to update the element with the result
  const root = ReactDOM.createRoot(resultDiv);
  root.render(<SubmissionResult success={success} />);
};